/* ========================================
   CUSTOM PRODUCT CARD - Universal Handler
   Works for: Collection pages, product recommendations, recently viewed
   ======================================== */

import { ensureDeliveryLocation } from '@theme/delivery-location-service';

(function() {
  'use strict';

  class CustomProductCard {
    constructor(card) {
      this.card = card;
      this.variantPills = card.querySelectorAll('.variant-pill');
      this.qtyInput = card.querySelector('.qty-input');
      this.qtyDecrease = card.querySelector('.qty-decrease');
      this.qtyIncrease = card.querySelector('.qty-increase');
      this.atcBtn = card.querySelector('.atc-btn');
      this.priceDisplay = card.querySelector('[data-price-display]');
      this.comparePriceDisplay = card.querySelector('[data-compare-price-display]');
      this.mainImage = card.querySelector('[data-main-image]');
      
      this.init();
    }

    init() {
      this.variantPills.forEach(pill => {
        pill.addEventListener('click', (e) => this.handleVariantChange(e));
      });

      this.qtyDecrease?.addEventListener('click', () => this.decreaseQty());
      this.qtyIncrease?.addEventListener('click', () => this.increaseQty());
      this.qtyInput?.addEventListener('change', (e) => this.validateQty(e));
      this.atcBtn?.addEventListener('click', () => this.addToCart());
    }

    handleVariantChange(e) {
      const pill = e.target;
      if (pill.classList.contains('disabled')) return;

      this.variantPills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');

      const price = parseInt(pill.dataset.price);
      const comparePrice = parseInt(pill.dataset.comparePrice);
      
      this.priceDisplay.textContent = `₹${Math.round(price / 100)}`;
      
      if (comparePrice > price) {
        this.priceDisplay.classList.add('solution-card__price--sale');
        this.comparePriceDisplay.textContent = `₹${Math.round(comparePrice / 100)}`;
        this.comparePriceDisplay.style.display = 'inline';
      } else {
        this.priceDisplay.classList.remove('solution-card__price--sale');
        this.comparePriceDisplay.style.display = 'none';
      }

      const imageSrc = pill.dataset.imageSrc;
      if (imageSrc && this.mainImage) {
        this.mainImage.src = imageSrc;
      }

      this.atcBtn.dataset.variantId = pill.dataset.variantId;
      this.atcBtn.disabled = pill.dataset.available === 'false';
      this.atcBtn.textContent = pill.dataset.available === 'true' ? 'Add to cart' : 'Sold Out';
    }

    decreaseQty() {
      const currentQty = parseInt(this.qtyInput.value);
      if (currentQty > 1) {
        this.qtyInput.value = currentQty - 1;
      }
    }

    increaseQty() {
      const currentQty = parseInt(this.qtyInput.value);
      const max = parseInt(this.qtyInput.max);
      if (currentQty < max) {
        this.qtyInput.value = currentQty + 1;
      }
    }

    validateQty(e) {
      let value = parseInt(e.target.value);
      const min = parseInt(e.target.min);
      const max = parseInt(e.target.max);
      
      if (isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      
      e.target.value = value;
    }

    async addToCart() {
      this.atcBtn.disabled = true;
      const originalText = this.atcBtn.textContent;
      this.atcBtn.textContent = 'Adding...';

      if (!(await ensureDeliveryLocation())) {
        this.atcBtn.textContent = originalText;
        this.atcBtn.disabled = false;
        return;
      }

      const variantId = this.atcBtn.dataset.variantId;
      const quantity = parseInt(this.qtyInput.value);

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: quantity }]
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        await this.refreshCartDrawer();
        this.openCartDrawer();
        
        this.atcBtn.textContent = '✓ Added';
        this.atcBtn.style.background = '#28a745';
        this.atcBtn.style.color = 'white';
        
        setTimeout(() => {
          this.atcBtn.textContent = originalText;
          this.atcBtn.style.background = '';
          this.atcBtn.style.color = '';
          this.atcBtn.disabled = false;
          this.qtyInput.value = 1;
        }, 1000);

      } catch (error) {
        console.error('Add to cart error:', error);
        this.atcBtn.textContent = 'Error';
        this.atcBtn.style.background = '#dc3545';
        this.atcBtn.style.color = 'white';
        
        setTimeout(() => {
          this.atcBtn.textContent = originalText;
          this.atcBtn.style.background = '';
          this.atcBtn.style.color = '';
          this.atcBtn.disabled = false;
        }, 1500);
      }
    }

    async refreshCartDrawer() {
      try {
        const cartItemsComponent = document.querySelector('cart-items-component[data-drawer]');
        if (!cartItemsComponent) {
          await this.updateCartCount();
          return;
        }

        const sectionId = cartItemsComponent.dataset.sectionId;
        
        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`),
          fetch(window.Shopify.routes.root + 'cart.js')
        ]);
        
        const [html, cart] = await Promise.all([
          sectionResponse.text(),
          cartResponse.json()
        ]);
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const currentDrawerInner = document.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');
        const newDrawerInner = doc.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');
        
        if (currentDrawerInner && newDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        const cartCountSelectors = [
          '[data-cart-count]',
          'cart-icon [data-cart-count]',
          '.cart-count-bubble'
        ];
        
        cartCountSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.classList.remove('hidden');
              el.style.display = '';
            }
          });
        });

      } catch (error) {
        console.error('Cart refresh error:', error);
        await this.updateCartCount();
      }
    }

    async updateCartCount() {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();
        
        const cartCountSelectors = [
          '[data-cart-count]',
          'cart-icon [data-cart-count]',
          '.cart-count-bubble'
        ];
        
        cartCountSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.classList.remove('hidden');
              el.style.display = '';
            }
          });
        });

      } catch (error) {
        console.error('Cart count update error:', error);
      }
    }

    openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }
        
        if (typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
          return;
        }
      }
      
      const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (cartTrigger) {
        cartTrigger.click();
      }
    }
  }

  // ========================================
  // Initialize all product cards
  // ========================================
  
  function initProductCards() {
    const cards = document.querySelectorAll('.solution-card[data-card-behavior="custom-product-card"]');
    console.log(`Initializing ${cards.length} product cards`);
    
    cards.forEach(card => {
      // Check if already initialized
      if (card.dataset.cardInitialized === 'true') {
        return;
      }
      
      new CustomProductCard(card);
      card.dataset.cardInitialized = 'true';
    });
  }

  // ========================================
  // Initial load
  // ========================================
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // ========================================
  // Re-initialize for dynamic content
  // ========================================
  
  // For infinite scroll on collection pages
  document.addEventListener('results-list:updated', () => {
    console.log('Collection page updated, re-initializing cards');
    initProductCards();
  });

  // For product recommendations (CRITICAL FIX)
  const productRecommendations = document.querySelector('product-recommendations');
  if (productRecommendations) {
    // Watch for changes in the recommendations section
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          console.log('Product recommendations loaded, initializing cards');
          setTimeout(initProductCards, 100); // Small delay to ensure DOM is ready
        }
      });
    });

    observer.observe(productRecommendations, {
      childList: true,
      subtree: true
    });
  }

  // Alternative: Listen for custom events from product-recommendations.js
  document.addEventListener('product-recommendations:loaded', () => {
    console.log('Product recommendations loaded event, initializing cards');
    setTimeout(initProductCards, 100);
  });

  // Fallback: Re-check every 2 seconds for new cards (for 10 seconds)
  let checkCount = 0;
  const checkInterval = setInterval(() => {
    const uninitializedCards = document.querySelectorAll('.solution-card:not([data-card-initialized="true"])');
    if (uninitializedCards.length > 0) {
      console.log(`Found ${uninitializedCards.length} uninitialized cards, initializing...`);
      initProductCards();
    }
    
    checkCount++;
    if (checkCount >= 5) {
      clearInterval(checkInterval);
    }
  }, 2000);

})();
